import { X } from "lucide-react";

import { Progress } from "@/components/ui/progress";

import { ReactComponent as CompleteIcon } from "@assets/svg/check-circled-filled.svg";
import { ReactComponent as DocumentIcon } from "@assets/svg/document-filled.svg";
import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { stripBundlePrefix } from "@utils/bundle-name";
import { formatFileSize } from "@utils/fileFormat";

// Define prop types for the component
type FilePreviewProps = {
  file: File;
  isSuccess?: boolean;
  isFailed?: boolean;
  isLoading?: boolean;
  uploadProgress?: number;
  onClear?: () => void;
};

// FilePreview component definition
const FilePreview = ({
  file,
  isSuccess = false,
  isFailed = false,
  isLoading = false,
  uploadProgress,
  onClear,
}: FilePreviewProps) => {
  return (
    <div
      className={`w-full rounded-xl ${isFailed ? "bg-destructive-50" : "bg-background-white"} px-2 py-3`}
    >
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
        <figure
          className={`rounded-full ${isFailed ? "bg-destructive-100" : "bg-accent-50"} p-2`}
        >
          <DocumentIcon
            className={`size-5 ${isFailed ? "[&_path]:fill-destructive" : "[&_path]:fill-accent"}`}
          />
        </figure>

        <div className="flex items-center gap-2">
          <span className="line-clamp-1 text-sm font-medium">
            {stripBundlePrefix(file.name)}
          </span>{" "}
          <span className="text-xs text-foreground-muted">
            {formatFileSize(file.size)}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {isFailed ? (
            <DocumentIcon className="size-4 [&_path]:fill-destructive" />
          ) : isLoading || uploadProgress !== undefined ? (
            <LoadingIcon className="size-4 animate-spin-ccw" />
          ) : (
            <CompleteIcon className="size-4 [&_path]:fill-success" />
          )}
          {onClear && (
            <button
              onClick={onClear}
              className="text-foreground-muted hover:text-foreground"
              aria-label="Remove file"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {uploadProgress !== undefined && uploadProgress < 100 && (
        <div className="mt-2 px-1">
          <Progress
            value={uploadProgress > 0 ? uploadProgress : undefined}
            className="h-2"
          />
        </div>
      )}
    </div>
  );
};

// Export the component
export { FilePreview };
